import { useState, useEffect, cloneElement } from 'react'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Icon } from '@iconify/react'

import { SharedReportPasswordGate } from './SharedReportPasswordGate.jsx'
import { CircleLoader } from '@/components/loader/CircleLoader.jsx'
import { AnalyticsService } from '@/services/analytics.js'
import { handleError } from '@/services/errorHandler.js'
import * as API from '@/services/api.js'

import OurAmazingLogo from '@/assets/logos/spring.png'

// Bumped to v2 because reportMeta gained adAccountIds + accountsMeta — old
// caches would otherwise hide the multi-account icons in the header until
// reauth.
const STORAGE_KEY_PREFIX = 'shared-report-token-v2-'

// Save token + meta to localStorage
const saveToStorage = (shareableReportId, token, reportMeta) => {
    try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${shareableReportId}`, JSON.stringify({ token, reportMeta }))
    } catch (e) {
        // localStorage might be full or disabled — silently ignore
    }
}

// Read token + meta from localStorage
const readFromStorage = (shareableReportId) => {
    try {
        const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${shareableReportId}`)
        if (!stored) {
            return null
        }
        return JSON.parse(stored)
    } catch (e) {
        return null
    }
}

// Clear stored token
const clearStorage = (shareableReportId) => {
    try {
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}${shareableReportId}`)
    } catch (e) {
        // ignore
    }
}

export default function SharedReportDataProvider({ children }) {

    const { shareableReportId } = useParams()

    const [isChecking, setIsChecking] = useState(true)
    const [needsPassword, setNeedsPassword] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [isAuthenticating, setIsAuthenticating] = useState(false)
    const [authError, setAuthError] = useState('')
    const [shareToken, setShareToken] = useState(null)
    const [reportMeta, setReportMeta] = useState(null)

    // On mount: try cached token first, then fall back to authentication
    useEffect(() => {
        const init = async () => {
            setIsChecking(true)
            AnalyticsService.page('shared-report-page-opened', { shareableReportId })

            // Check localStorage for a cached token
            const cached = readFromStorage(shareableReportId)
            if (cached?.token && cached?.reportMeta) {
                // Trust the cached token — if it's expired the /data call will fail
                // and the user will need to refresh and re-authenticate
                setShareToken(cached.token)
                setReportMeta(cached.reportMeta)
                setIsChecking(false)
                return
            }

            // No cached token — try no-password auth (works for read-only reports)
            try {
                const { data } = await API.authenticateShareableReport(shareableReportId, null)
                setShareToken(data.token)
                setReportMeta(data)
                saveToStorage(shareableReportId, data.token, data)
                AnalyticsService.page('shared-report-authenticated', { shareableReportId, passwordRequired: false })
            } catch (e) {
                const errorCode = e?.code || e?.statusCode
                const errorName = e?.name
                if (errorCode === 404 || errorName === 'shareable-report-not-found') {
                    setNotFound(true)
                } else if (errorCode === 401 || errorName === 'shareable-report-wrong-credentials') {
                    setNeedsPassword(true)
                } else {
                    setNotFound(true)
                    handleError(e)
                }
            } finally {
                setIsChecking(false)
            }
        }

        init()
    }, [shareableReportId])

    const handleAuthenticate = async (password) => {
        try {
            setIsAuthenticating(true)
            setAuthError('')
            const { data } = await API.authenticateShareableReport(shareableReportId, password)
            setShareToken(data.token)
            setReportMeta(data)
            saveToStorage(shareableReportId, data.token, data)
            AnalyticsService.page('shared-report-authenticated', { shareableReportId, passwordRequired: true })
        } catch (e) {
            const errorCode = e?.code || e?.statusCode
            const errorName = e?.name
            if (errorCode === 401 || errorName === 'shareable-report-wrong-credentials') {
                setAuthError('Incorrect password. Please try again.')
            } else if (errorCode === 404 || errorName === 'shareable-report-not-found') {
                setNotFound(true)
            } else {
                setAuthError('Something went wrong. Please try again.')
                handleError(e)
            }
        } finally {
            setIsAuthenticating(false)
        }
    }

    // Initial check in progress
    if (isChecking) {
        return (
            <div className="min-h-screen bg-main-surface bg-center bg-cover bg-no-repeat flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                >
                    <CircleLoader className="h-6 w-6 animate-spin text-primary" />
                </motion.div>
            </div>
        )
    }

    // Report not found or deleted
    if (notFound) {
        return (
            <div className="min-h-screen bg-main-surface bg-center bg-cover bg-no-repeat flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="flex flex-col items-center text-center p-8 max-w-sm"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1, duration: 0.3 }}
                    >
                        <Icon icon="mdi:link-off" className="h-16 w-16 text-muted-foreground/30 mb-4" />
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg font-medium text-foreground"
                    >
                        Report Not Available
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-sm text-muted-foreground mt-2"
                    >
                        This shared report may have been deleted or the link is no longer valid.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="text-xs text-muted-foreground/60 mt-4"
                    >
                        Please contact the person who shared this report with you.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="flex items-center gap-1 mt-8 cursor-pointer"
                        onClick={() => window.open('https://upspring.ai', '_blank')}
                    >
                        <span className="text-xs text-muted-foreground/60">Powered by</span>
                        <img src={OurAmazingLogo} alt="upspring-ai-logo" className="w-16" />
                    </motion.div>
                </motion.div>
            </div>
        )
    }

    // Password required — show the gate
    if (needsPassword && (!shareToken || !reportMeta)) {
        return (
            <SharedReportPasswordGate
                onAuthenticate={handleAuthenticate}
                isLoading={isAuthenticating}
                error={authError}
            />
        )
    }

    // Authenticated — render the report
    if (shareToken && reportMeta) {
        return cloneElement(children, {
            shareableReportId,
            shareToken,
            reportMeta,
        })
    }

    return null
}
